/**
 * Structured data schemas for Receipt Maker
 *
 * The Organization and WebSite nodes are injected globally (see app/layout.tsx)
 * with stable @id values so that every other schema on the site (Article,
 * WebApplication, WebPage, FAQPage, BreadcrumbList) can reference them by @id
 * instead of redeclaring the publisher inline. This builds a single connected
 * entity graph that answer engines can reason over.
 */

export const BASE_URL = 'https://www.tryreceiptmaker.com';

// Stable @id anchors for the core entity graph
export const ORG_ID = `${BASE_URL}/#organization`;
export const WEBSITE_ID = `${BASE_URL}/#website`;
export const LOGO_ID = `${BASE_URL}/#logo`;
export const WEBAPP_ID = `${BASE_URL}/#webapplication`;

// Last meaningful content/structure update of the site as a whole.
// Bump when the global schema or sitewide content changes materially.
export const SITE_LAST_MODIFIED = '2026-05-29';

// Public social profiles (used for sameAs entity disambiguation)
export const SOCIAL_PROFILES = [
  'https://www.youtube.com/@ReceiptMaker',
  'https://www.pinterest.com/receiptmaker/',
  'https://www.instagram.com/tryreceiptmaker/',
];

// Topical entities the site is an authority on (knowsAbout)
export const KNOWS_ABOUT = [
  'Receipts',
  'Receipt templates',
  'Online receipt generation',
  'Proof of purchase',
  'Itemized receipts',
  'Sales receipts',
  'Invoices',
  'Thermal paper receipts',
  'Point of sale (POS) receipts',
  'Expense tracking',
  'Business expense documentation',
  'Tax record keeping',
  'PDF receipt export',
];

// Reusable founder/Person node for Reggie Jacobs (the public-facing founder)
export const founderPerson = {
  '@type': 'Person',
  '@id': `${BASE_URL}/authors/reggie-jacobs#person`,
  name: 'Reggie Jacobs',
  jobTitle: 'Founder of Receipt Maker & Document Management Expert',
  url: `${BASE_URL}/authors/reggie-jacobs`,
  image: `${BASE_URL}/authors/reggie-jacobs.jpg`,
};

// Organization Schema - injected globally on every page via layout.tsx
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Receipt Maker',
  alternateName: ['Receipt Maker - Free Online Receipt Maker', 'TryReceiptMaker'],
  url: BASE_URL,
  logo: {
    '@type': 'ImageObject',
    '@id': LOGO_ID,
    url: `${BASE_URL}/Receipt%20Maker%20logo.svg`,
    caption: 'Receipt Maker',
    width: 512,
    height: 512,
  },
  image: `${BASE_URL}/og-default.png`,
  description:
    'Receipt Maker is a free online receipt maker for business, creative, and educational purposes. Create, customize, and download receipts as PDF or PNG in seconds. No sign-up required.',
  foundingDate: '2020',
  founders: [founderPerson],
  knowsAbout: KNOWS_ABOUT,
  sameAs: SOCIAL_PROFILES,
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'info@tryreceiptmaker.com',
      url: `${BASE_URL}/contact`,
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'Editorial',
      email: 'info@tryreceiptmaker.com',
      url: `${BASE_URL}/contact`,
      availableLanguage: ['English'],
    },
  ],
  areaServed: {
    '@type': 'Place',
    name: 'Worldwide',
  },
  actionableFeedbackPolicy: `${BASE_URL}/contact`,
};

// WebSite Schema - injected globally on every page via layout.tsx
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'Receipt Maker',
  alternateName: 'Receipt Maker - Free Online Receipt Maker',
  url: BASE_URL,
  description:
    'Receipt Maker is a free online receipt maker for business, creative, and educational purposes. Create, customize, and download receipts as PDF or PNG in seconds.',
  inLanguage: 'en',
  dateModified: SITE_LAST_MODIFIED,
  publisher: { '@id': ORG_ID },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/templates?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

// WebApplication Schema - homepage and builder page
export const webApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  '@id': WEBAPP_ID,
  name: 'Receipt Maker',
  alternateName: 'Receipt Maker Free Online Receipt Maker',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Receipt Maker',
  operatingSystem: 'Web Browser',
  url: `${BASE_URL}/builder`,
  description:
    'Receipt Maker is a free online receipt maker for business, creative, and educational purposes. Customize every detail and download receipts as PDF or PNG. 190+ templates, no sign-up required.',
  isPartOf: { '@id': WEBSITE_ID },
  publisher: { '@id': ORG_ID },
  creator: { '@id': ORG_ID },
  screenshot: [
    `${BASE_URL}/hero-receipt.png`,
    `${BASE_URL}/create-receipt-now.png`,
  ],
  featureList: [
    'Free Receipt Maker',
    'Customizable Receipt Templates',
    'PDF Receipt Download',
    'PNG Receipt Download',
    'Editable Receipt Template',
    'Thermal Paper Style',
    'POS Receipt Format',
    'Invoice & Bill Support',
    'Custom Receipt Builder',
    'Unlimited Receipt Creation',
    'No Account Required',
    'Realistic Receipt Textures',
    'Multiple Font Options',
    'Itemized Line Items',
    'Payment Method Selection',
    'Tax Calculation',
    'Business Information Customization',
    'Custom Logo Support',
  ],
  softwareRequirements: 'Modern web browser with JavaScript enabled',
  datePublished: '2020-01-01T00:00:00+00:00',
  dateModified: `${SITE_LAST_MODIFIED}T00:00:00+00:00`,
  offers: {
    '@type': 'Offer',
    priceCurrency: 'USD',
    price: '0',
    description:
      'Free access to the receipt generator with unlimited receipt creation. Premium features available with subscription-based pricing.',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '305',
    bestRating: '5',
    worstRating: '1',
  },
};

/**
 * Build a SpeakableSpecification node. Tells answer engines which CSS selectors
 * contain the concise, quotable answer text on a page.
 */
export function getSpeakable(cssSelector: string[]) {
  return {
    '@type': 'SpeakableSpecification',
    cssSelector,
  };
}
